import React, { useEffect, useRef } from 'react';
import { useRoutes } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import routes from './config/routes';
import notificationService from './services/notification-service';
import { useAuth } from './contexts/AuthContext';
import useAppUpdater from './hooks/useAppUpdater';
import ErrorBoundary from './components/common/ErrorBoundary';
import OfflineOverlay from './components/common/OfflineOverlay';
import logger from './shared/utils/logger';

/**
 * Register service worker for web push notifications
 */
const registerServiceWorker = async () => {
  if ('serviceWorker' in navigator) {
    try {
      const registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js');
      return registration;
    } catch (error: unknown) {
      throw error;
    }
  }
  throw new Error('Service workers are not supported in this browser');
};

/**
 * App component wrapper that handles initialization
 */
const AppWithAuth: React.FC = () => {
  const { user } = useAuth();
  const updater = useAppUpdater();
  const updateCheckedRef = useRef<boolean>(false);
  
  // Check for updates once after login
  useEffect(() => {
    if (user && !updateCheckedRef.current) {
      // Add a small delay to ensure everything is initialized
      const timer = setTimeout(() => {
        updater.checkForUpdates();
        updateCheckedRef.current = true;
      }, 3000);
      
      return () => clearTimeout(timer);
    }
  }, [user, updater]);
  
  // Initialize notifications when user is authenticated
  useEffect(() => {
    let serviceWorkerRegistration: ServiceWorkerRegistration | null = null;
    
    const initializeNotifications = async () => {
      if (user?.id) {
        try {
          // Register service worker for web push
          if ('serviceWorker' in navigator) {
            serviceWorkerRegistration = await registerServiceWorker();
          }
          
          // Initialize notification service
          await notificationService.init(user.id.toString());
        } catch (error: unknown) {
          logger.error('Failed to initialize notification service:', error);
        }
      }
    };
    
    initializeNotifications();
    
    // Cleanup on unmount
    return () => {
      notificationService.disconnect();
      
      // Unregister service worker if needed
      if (serviceWorkerRegistration) {
        serviceWorkerRegistration.unregister().catch((...args) => logger.error(...args));
      }
    };
  }, [user]);
  
  const routing = useRoutes(routes);
  
  return (
    <ErrorBoundary>
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
      <OfflineOverlay />
      {routing}
    </ErrorBoundary>
  );
};

/**
 * Main App component that renders routes
 */
const App: React.FC = () => {
  return <AppWithAuth />;
};

export default App; 